/**
 * SanctaMessage — one message bubble (user or assistant).
 *
 * Handles assistant-message block parsing (product cards, comparison, cart
 * preview, sibascore breakdown) via `SanctaBlocks`, markdown rendering, and
 * the per-message "Read aloud" TTS button.
 *
 * Extracted from SanctaChatbot.tsx.
 */
import React from "react";
import { motion } from "framer-motion";
import { Loader2, Volume2, Square } from "lucide-react";
import { api } from "@/lib/api";
import { SanctaMarkdown } from "@/components/sancta/SanctaMarkdown";
import { parseSanctaMessage, SanctaBlocks } from "@/components/sancta/SanctaMessageBlocks";

export interface SanctaMessageData {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: number;
  detectedLang?: string;
}

interface Props {
  index: number;
  msg: SanctaMessageData;
  isLast: boolean;
  streaming: boolean;
  variant: "widget" | "page";
  chatbotName: string;
  chatbotAccent: string;
  conversationId: string | null;
  detectionCode?: string;
  fallbackLang: string;               // langCode for TTS when no detection
  thinkingLabel: string;              // strings.thinking
  tts: {
    supported: boolean;
    speaking: boolean;
    currentId: string | null;
    speak: (id: string, text: string, lang: string) => void;
    stop: () => void;
  };
}

export const SanctaMessage: React.FC<Props> = ({
  index, msg, isLast, streaming, variant, chatbotName, chatbotAccent,
  conversationId, detectionCode, fallbackLang, thinkingLabel, tts,
}) => {
  const isLastAssistant = msg.role === "assistant" && isLast;
  const isStreamingThisRow = isLastAssistant && streaming;
  const canRead =
    msg.role === "assistant" &&
    !isStreamingThisRow &&
    msg.content.trim().length > 0 &&
    tts.supported;
  const isReadingThis = tts.speaking && tts.currentId === msg.id;
  const readLang = msg.detectedLang || detectionCode || fallbackLang;

  // Parse assistant messages for inline block tags so product cards /
  // comparison / cart-preview render inside the chat scroll.
  const segments = msg.role === "assistant" ? parseSanctaMessage(msg.content || "") : null;

  return (
    <motion.div
      key={msg.id || index}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start items-start gap-2"}`}
      data-testid={`sancta-msg-${index}`}
    >
      {msg.role === "assistant" && (
        <div
          className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white text-[11px] font-semibold shrink-0"
          style={{ backgroundImage: `linear-gradient(135deg, ${chatbotAccent}, #d946ef)` }}
        >
          {chatbotName.charAt(0)}
        </div>
      )}
      <div className="max-w-[85%]">
        <div
          className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap
                      ${msg.role === "user"
                        ? "bg-neutral-900 text-white rounded-br-md"
                        : (variant === "page"
                            ? "bg-neutral-100 text-neutral-800 dark:bg-white/10 dark:text-slate-100 rounded-bl-md"
                            : "bg-neutral-100 text-neutral-800 rounded-bl-md")}`}
        >
          {segments ? (
            <SanctaBlocks
              segments={segments}
              conversationId={conversationId}
              renderText={(t) => <SanctaMarkdown text={t} />}
              onShortlistChange={(slugs) => {
                if (!conversationId || !slugs.length) return;
                // Persist the numbered shortlist so the LLM can resolve
                // "add number 3" on the next turn.
                api.post("/sancta/agent/shortlist", { conversation_id: conversationId, slugs })
                  .catch(() => { /* best-effort */ });
              }}
            />
          ) : msg.content ? (
            <SanctaMarkdown text={msg.content} />
          ) : isStreamingThisRow ? (
            <span className={`inline-flex items-center gap-1 ${variant === "page" ? "text-neutral-500 dark:text-slate-300" : "text-neutral-500"}`}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {thinkingLabel}
            </span>
          ) : ""}
        </div>
        {canRead && (
          <button
            type="button"
            data-testid={`sancta-speak-${index}`}
            onClick={() => {
              if (isReadingThis) tts.stop();
              else tts.speak(msg.id, msg.content, readLang);
            }}
            aria-pressed={isReadingThis}
            aria-label={isReadingThis ? "Stop reading aloud" : "Read this reply aloud"}
            title={isReadingThis ? "Tap to stop" : `Read aloud in ${readLang.toUpperCase()}`}
            className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
              isReadingThis
                ? "bg-fuchsia-500 text-white animate-pulse"
                : variant === "page"
                    ? "bg-white text-neutral-500 border border-neutral-200 hover:border-fuchsia-400 hover:text-fuchsia-600 dark:bg-white/10 dark:text-slate-300 dark:border-white/10 dark:hover:border-fuchsia-400 dark:hover:text-fuchsia-300"
                    : "bg-white text-neutral-500 border border-neutral-200 hover:border-fuchsia-400 hover:text-fuchsia-600"
            }`}
          >
            {isReadingThis ? <Square className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
            {isReadingThis ? "Stop" : "Read aloud"}
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default SanctaMessage;
